import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { PreInitSettingsService } from '../../../../core/services/settings/pre-init-setting.service';
import { ISelectedContract, LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { UUID } from 'angular2-uuid';
import { BillingProfileDataService } from 'src/app/core/services/billing-profile-data.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ContractDetailsPDFService } from 'src/app/core/services/contract-details-pdf.service';
import { UtilityService } from 'src/app/core/services/utility.service';
import { BILLINGANDPAYMENT, VIEWCONTRACTDETAILS } from 'src/app/core/constants/feature-key.constants';
import { ActivatedRoute } from '@angular/router';
import { IAccount } from 'src/app/model/sitefinity/IAccount';
import { GoogleAnalyticsService } from 'src/app/core/services/google-tag-manager.service';
import { ADPResourceService } from 'digital-core';
import { GlobalModalWindowService } from 'src/app/core/services/global-modal-window.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { OktaUserProfile } from 'src/app/model/okta/userClaims';

// workaround to open PDF
window.URL = window.URL || window.webkitURL;

@Component({
  selector: 'pgh-account-details',
  templateUrl: './account-details.component.html',
  styleUrls: ['./account-details.component.scss'],
})
export class AccountDetailsComponent implements OnInit, OnDestroy {
  isNative: boolean = false;
  isViewContractDetailsEnabled: boolean = undefined;
  accountPageSettings: IAccount = null;
  offset: number;
  fileURL: string;
  selectedContract: ISelectedContract;
  isClicked: boolean = false;
  paymentInfoData: any = {};
  isLoading = true;
  programCode: string = '';
  clientId: string = '';
  associateNumber: string = '';
  trackingId = UUID.UUID();
  displayStreetAddress1: string = '';
  displayCityStateZip: string = '';
  @Input()
  IsPdfView: boolean = false; // tslint:disable-line:variable-name
  fileContent: any;
  fileName: any;
  base64: any = '';
  contractNumber: string;
  isContractMissing: boolean = false;
  isBillingAndPaymentEnabled: boolean = true;
  isViewContractFromSitefinity = false;
  isClientSoldType = false;
  isSpoeEnabled = false;
  eprismContract: any;
  eprismPrimaryPhone: string;
  eprismAuthorizedPhones: string;
  private destroy$ = new Subject<boolean>();

  constructor(
    public route: ActivatedRoute,
    private billingProfileDataService: BillingProfileDataService,
    private preInitSettingsService: PreInitSettingsService,
    private loggedInUserSettings: LoggedInUserSettings,
    private contractDetailsPDFService: ContractDetailsPDFService,
    private utilityService: UtilityService,
    private googleService: GoogleAnalyticsService,
    private errorDialogService: GlobalModalWindowService,
    private storageService: StorageService,
    public resourceService: ADPResourceService
  ) {
    this.route.queryParams.subscribe((params) => {
      this.isNative = (params.isNative && params.isNative.toLowerCase().trim() === 'true') || this.utilityService.isNativeApp;
    });
    this.programCode = this.preInitSettingsService.getSettings().middleWare.programCode;
    this.clientId = this.preInitSettingsService.getSettings().middleWare.clientId;
    this.associateNumber = this.preInitSettingsService.getSettings().middleWare.associateNumber;
  }

  ngOnInit() {
    this.offset = new Date().getTimezoneOffset();
    this.loggedInUserSettings.$selectedContract.pipe(takeUntil(this.destroy$)).subscribe((selectedContract) => {
      if (selectedContract && !this.selectedContract) {
        this.isClientSoldType = this.resourceService.isPortalSettingTrue('Global_Client_IsClientSoldType');
        this.isViewContractFromSitefinity = this.resourceService.isPortalSettingTrue('Global_ViewContract_From_Sitefinity');
        this.selectedContract = selectedContract;
        this.contractNumber = selectedContract.id;
        if (this.isViewContractDetailsEnabled === undefined) {
          this.isViewContractDetailsEnabled = this.loggedInUserSettings.isFeatureEnabled(selectedContract.dealerId, VIEWCONTRACTDETAILS) && selectedContract.isPrimary;
          this.isBillingAndPaymentEnabled = this.loggedInUserSettings.isFeatureEnabled(selectedContract.dealerId, BILLINGANDPAYMENT);
        }
        const dealerId = selectedContract.dealerId?.trim()?.toLowerCase();
        this.setDisplayAddress();
        if (dealerId === 'lptp' || dealerId === 'dtepg') {
          this.paymentInfoData.contractNumber = this.contractNumber;
          this.paymentInfoData.ContractStartDate = this.selectedContract.startDate;
          this.isLoading = false;
        } else {
          if (this.isBillingAndPaymentEnabled) {
            this.getBillingInfo();
          } else {
            this.paymentInfoData.contractNumber = this.contractNumber;
            this.isLoading = false;
          }
        }
      }
    });
    let eprismContracts = this.storageService.getLocal<any[]>('EprismContracts');
    let oktaProfile = this.storageService.getLocal<OktaUserProfile>('okta-user-profile');
    this.isSpoeEnabled = this.resourceService.isPortalSettingTrue('Global_SPOE_Enabled');
    if (eprismContracts?.length) {
      this.eprismContract = eprismContracts.find(x => x.policyIdentifier.mobileNumber == oktaProfile.primaryPhone);
      this.eprismPrimaryPhone = eprismContracts.find(x => x.policyHolderInfo.pahPhone == x.policyIdentifier.mobileNumber)?.policyIdentifier?.mobileNumber;
      this.eprismAuthorizedPhones = eprismContracts.filter(x => x.policyHolderInfo.pahPhone != x.policyIdentifier.mobileNumber).map(x => x?.policyIdentifier?.mobileNumber).join(', ');
    }
  }

  getBillingInfo() {
    this.isLoading = true;
    const startTime = Date.now();
    this.billingProfileDataService
      .getBillingInfo(
        this.selectedContract?.zipCode,
        this.programCode,
        this.selectedContract?.id,
        this.clientId,
        this.trackingId,
        this.associateNumber
      )
      .pipe(finalize(() => {}))
      .subscribe({
        next: (data) => {
          this.googleService.apiPerformanceEmitter('Get Payment', startTime);
          this.paymentInfoData = data;
          this.paymentInfoData.contractNumber = this.selectedContract?.id;
          this.isLoading = false;
        },
        error: (error) => {
          this.googleService.apiPerformanceEmitter('Get Payment Failed', startTime);
          this.isLoading = false;
          throw error;
        },
      });
  }

  setDisplayAddress() {
    if (this.selectedContract && this.selectedContract.addressLine1 && this.selectedContract.state && this.selectedContract.zipCode) {
      this.displayStreetAddress1 = this.selectedContract?.addressLine1;
      this.displayCityStateZip = this.selectedContract?.city + ', ' + this.selectedContract?.state + ' ' + this.selectedContract?.zipCode;
    } else {
      this.displayStreetAddress1 = '';
      this.displayCityStateZip = '';
    }
  }

  loadPdf() {
    const hasMultipleClient = this.resourceService.isPortalSettingTrue('Global_HasMultipleClient');
    if (this.fileURL) {
      window.open(this.fileURL);
    } else if (this.isViewContractFromSitefinity) {
      if (hasMultipleClient) {
        this.fileURL = this.resourceService.getPortalSetting('URL_Doc_' + this.selectedContract.masterPlanCode)?.Value;
        if (!this.fileURL) {
          this.errorDialogService.displaySimpleErrorModal('Global_Error_PlanCode_Doc_NotFound');
          return;
        }
        window.open(this.fileURL);
      } else if (this.isClientSoldType) {
        this.fileURL = this.resourceService.getPortalSetting('URL_Terms_And_Conditions').Value;
        window.open(this.fileURL);
      }
    } else {
      this.isContractMissing = true;
      this.contractDetailsPDFService
        .getCoverageDocument(this.contractNumber, this.clientId, this.trackingId, this.associateNumber)
        .subscribe(
          (res) => {
            if (res.FileContent) {
              this.fileContent = res.FileContent;
              this.fileName = res.FileName;
            }
            this.preparePdf();
            this.isContractMissing = false;
          },
          (error) => {
            this.isContractMissing = false;
            throw error;
          }
        );
    }
  }

  preparePdf() {
    if (this.IsPdfView) {
      this.base64 = this.fileContent;
    }
    if (this.isNative) {
      const rnWindow = window as any;
      if (rnWindow.ReactNativeWebView) {
        const message = JSON.stringify({ action: 'openContract', args: {} });
        rnWindow.ReactNativeWebView.postMessage(message);
      }
    } else {
      const byteCharacters = atob(this.fileContent);
      const byteNumbers = new Array(byteCharacters.length);

      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      const file = new Blob([byteArray], { type: 'application/pdf' });
      const URL = window.URL || window.webkitURL;
      this.fileURL = URL.createObjectURL(file);
      window.open(this.fileURL);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  getViewLabel(item: string) {
    const label = this.resourceService.getViewLabel(item) ?? '';
    return this.resourceService.transformContentData(label);
  }
}
